import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IntroService } from 'src/app/services/intro.service';
import { environment } from 'src/environments/environment';

// Geometry of the D20 hexagon silhouette inside the 512px icon, normalised to its 202px radius.
const VERTICES = [[256, 55], [427, 156], [427, 348], [256, 458], [85, 347], [85, 159]]
  .map(([x, y]) => [(x - 256) / 202, (y - 256) / 202]);
const SAMPLES = 1800;

// Where each piece sits inside the 2095x632 lockup that the header logo SVG shares with the intro logo PNG.
const LOCKUP_HEIGHT = 632;
const LOCKUP_DIE = { cx: 276.5, cy: 315.5, radius: 295 };
const LOCKUP_WORD = { x: 535, y: 195, w: 1560, h: 260 };

const START_DELAY = 400;
const ROLL_DURATION = 4400;
const HOLD = 700;
const FLY = 900;
const SKIP_FLY = 650;
const FADE = 500;
const REDUCED_HOLD = 1200;

interface Frame { dieX: number; dieY: number; radius: number; wordX: number; wordY: number; wordW: number; wordH: number; }

@Component({
  selector: 'app-intro-splash',
  templateUrl: './intro-splash.component.html',
  styleUrls: ['./intro-splash.component.scss'],
  standalone: false
})
export class IntroSplashComponent implements OnInit, OnDestroy {
  @ViewChild('canvas') set canvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref) {
      this.start(ref.nativeElement);
    }
  }
  @ViewChild('backdrop') backdrop?: ElementRef<HTMLDivElement>;

  active = false;
  leaving = false;
  flying = false;

  private sub?: Subscription;
  private canvas?: HTMLCanvasElement;
  private ctx?: CanvasRenderingContext2D | null;
  private resizeObserver?: ResizeObserver;
  private raf = 0;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private w = 0;
  private h = 0;
  private ready = false;
  private reduced = false;
  private icon = new Image();
  private logo = new Image();
  private headerLogo: HTMLElement | null = null;

  // Timeline, in performance.now() milliseconds.
  private rollStart = 0;
  private flyStart = Infinity;
  private flyDuration = FLY;
  private rollDone = false;
  private now = 0;

  // Precomputed roll path: rotation angle, hexagon height above centre, and arc distance travelled.
  private angles: number[] = [];
  private heights: number[] = [];
  private distances: number[] = [0];

  constructor(
    private introService: IntroService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    for (let i = 0; i <= SAMPLES; i++) {
      const a = (1 - i / SAMPLES) * Math.PI * 3;
      this.angles.push(a);
      this.heights.push(Math.max(...VERTICES.map(([x, y]) => x * Math.sin(a) + y * Math.cos(a))));
      if (i) {
        this.distances.push(
          this.distances[i - 1] + (this.heights[i] + this.heights[i - 1]) * 0.5 * Math.PI * 3 / SAMPLES,
        );
      }
    }
  }

  ngOnInit(): void {
    this.sub = this.introService.active$.subscribe((active) => {
      if (active && !this.active) {
        this.leaving = false;
        this.flying = false;
        this.active = true;
        this.cdr.detectChanges();
      } else if (!active && this.active) {
        this.teardown();
        this.active = false;
        this.cdr.detectChanges();
      }
    });

    // Dev-only preview: append ?intro to any URL to replay the post-login intro.
    if (!environment.production && new URLSearchParams(location.search).has('intro')) {
      this.introService.play();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.teardown();
  }

  @HostListener('document:keydown.escape')
  skip() {
    if (!this.active || this.leaving || this.flying) {
      return;
    }
    if (!this.ready) {
      this.fadeOut();
      return;
    }
    // Snap the die to its resting frame, then take the quick flight into the header.
    this.rollDone = true;
    this.beginExit(performance.now(), SKIP_FLY);
  }

  private start(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.ready = false;
    this.rollDone = this.reduced;
    this.flyStart = Infinity;

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.size());
      this.resizeObserver.observe(canvas);
      this.size();

      Promise.all([
        this.load(this.icon, 'assets/images/intro/worldtumbler-intro-icon.png'),
        this.load(this.logo, 'assets/images/intro/worldtumbler-intro-logo.png'),
      ]).then(
        () => {
          if (!this.active || this.leaving) return;
          this.ready = true;
          this.play();
        },
        // Never trap the user behind a broken intro.
        () => this.ngZone.run(() => this.introService.finish()),
      );
    });
  }

  private load(img: HTMLImageElement, src: string): Promise<void> {
    if (img.complete && img.naturalWidth && img.src.endsWith(src)) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = src;
    });
  }

  private play() {
    const t0 = performance.now();
    this.rollStart = t0 + START_DELAY;
    const exitAt = this.reduced ? t0 + REDUCED_HOLD : this.rollStart + ROLL_DURATION + HOLD;
    this.timers.push(setTimeout(() => this.ngZone.run(() => this.beginExit(performance.now(), FLY)), exitAt - t0));
    const tick = (now: number) => {
      this.now = now;
      this.draw();
      if (this.flyStart !== Infinity && now - this.flyStart >= this.flyDuration) {
        this.ngZone.run(() => this.land());
        return;
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private beginExit(now: number, duration: number) {
    if (this.flying || this.leaving) return;
    this.headerLogo = this.findHeaderLogo();
    if (this.reduced || !this.headerLogo) {
      this.fadeOut();
      return;
    }
    // The canvas copy stands in for the header logo until it lands.
    this.headerLogo.style.visibility = 'hidden';
    this.flyStart = now;
    this.flyDuration = duration;
    this.flying = true;
    this.cdr.detectChanges();
  }

  private land() {
    cancelAnimationFrame(this.raf);
    this.restoreHeaderLogo();
    this.introService.finish();
  }

  private fadeOut() {
    this.leaving = true;
    this.cdr.detectChanges();
    this.timers.push(setTimeout(() => this.introService.finish(), FADE));
  }

  private findHeaderLogo(): HTMLElement | null {
    const el = document.querySelector<HTMLElement>('header #logo-img');
    const rect = el?.getBoundingClientRect();
    return el && rect && rect.height > 0 && rect.bottom > 0 ? el : null;
  }

  private restoreHeaderLogo() {
    if (this.headerLogo) {
      this.headerLogo.style.visibility = '';
      this.headerLogo = null;
    }
  }

  private size() {
    if (!this.canvas || !this.ctx) return;
    this.w = this.canvas.clientWidth;
    this.h = this.canvas.clientHeight;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    this.canvas.width = this.w * dpr;
    this.canvas.height = this.h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.draw();
  }

  /** Lockup layout on the intro stage: a centred 10:3 band, max 1100px wide, with a 16px gutter. */
  private stageFrame(index: number, p: number): Frame & { revealLeft: number } {
    const sw = Math.min(this.w - 32, 1100);
    const sh = sw * 0.3;
    const sx = (this.w - sw) / 2;
    const sy = (this.h - sh) / 2;
    const { heights, distances } = this;
    const radius = sw * 0.87 / (distances[SAMPLES] - 1.6);
    const dieX = sx + sw + 1.6 * radius - distances[index] * radius;
    const dieY = sy + sh / 2 + (heights[SAMPLES] - heights[index]) * radius;
    const wordW = sw * 0.7;
    const wordH = wordW * LOCKUP_WORD.h / LOCKUP_WORD.w;
    const revealLeft = p === 1 ? 0 : Math.min(this.w, Math.max(0, dieX + radius * 0.18));
    return { dieX, dieY, radius, wordX: sx + sw * 0.27, wordY: sy + sh / 2 - wordH / 2, wordW, wordH, revealLeft };
  }

  /** Lockup layout exactly over the header's logo image. */
  private headerFrame(): Frame | null {
    const rect = this.headerLogo?.getBoundingClientRect();
    if (!rect) return null;
    const s = rect.height / LOCKUP_HEIGHT;
    return {
      dieX: rect.left + LOCKUP_DIE.cx * s,
      dieY: rect.top + LOCKUP_DIE.cy * s,
      radius: LOCKUP_DIE.radius * s,
      wordX: rect.left + LOCKUP_WORD.x * s,
      wordY: rect.top + LOCKUP_WORD.y * s,
      wordW: LOCKUP_WORD.w * s,
      wordH: LOCKUP_WORD.h * s,
    };
  }

  private draw() {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.w, this.h);
    if (!this.ready) return;

    const t = this.now;
    if (!this.rollDone && t < this.rollStart) return;
    const p = this.rollDone ? 1 : Math.min(1, (t - this.rollStart) / ROLL_DURATION);
    const u = p * p * (3 - 2 * p);
    const index = Math.min(SAMPLES, Math.floor(u * SAMPLES));
    const stage = this.stageFrame(index, p);

    let frame: Frame = stage;
    if (this.flyStart !== Infinity) {
      const f = Math.min(1, Math.max(0, (t - this.flyStart) / this.flyDuration));
      const k = f < 0.5 ? 4 * f * f * f : 1 - Math.pow(-2 * f + 2, 3) / 2;
      const target = this.headerFrame();
      if (target) {
        const lerp = (a: number, b: number) => a + (b - a) * k;
        frame = {
          dieX: lerp(stage.dieX, target.dieX),
          dieY: lerp(stage.dieY, target.dieY),
          radius: lerp(stage.radius, target.radius),
          wordX: lerp(stage.wordX, target.wordX),
          wordY: lerp(stage.wordY, target.wordY),
          wordW: lerp(stage.wordW, target.wordW),
          wordH: lerp(stage.wordH, target.wordH),
        };
      }
      // The navy curtain rises with the logo; its last strip lands on the matching header bar.
      if (this.backdrop) {
        this.backdrop.nativeElement.style.transform = `translateY(${-100 * k}%)`;
      }
    }

    // The wordmark is uncovered behind the die as it rolls right to left.
    ctx.save();
    ctx.beginPath();
    ctx.rect(stage.revealLeft, 0, this.w - stage.revealLeft, this.h);
    ctx.clip();
    ctx.drawImage(this.logo, LOCKUP_WORD.x, LOCKUP_WORD.y, LOCKUP_WORD.w, LOCKUP_WORD.h,
      frame.wordX, frame.wordY, frame.wordW, frame.wordH);
    ctx.restore();

    // Clip away the icon's square backing so only the die silhouette rolls.
    ctx.save();
    ctx.translate(frame.dieX, frame.dieY);
    ctx.rotate(this.angles[index]);
    ctx.scale(frame.radius / 202, frame.radius / 202);
    ctx.beginPath();
    VERTICES.forEach(([vx, vy], i) => (i ? ctx.lineTo(vx * 202, vy * 202) : ctx.moveTo(vx * 202, vy * 202)));
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(this.icon, -256, -256, 512, 512);
    ctx.restore();
  }

  private teardown() {
    cancelAnimationFrame(this.raf);
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.restoreHeaderLogo();
    this.canvas = undefined;
    this.ctx = undefined;
  }
}
